<template>
    <div
        class="uik-progress"
        :class="{'uik-progress--full-width': isFullWidth}"
    >
        <div
            v-if="type === 'line' || type === 'steps'"
            class="uik-progress__line"
        >
            <UikDataLabel
                v-if="label"
                class="uik-progress__line-label"
                :text="label"
                :color="color"
                :fill="labelFill"
                :size="getLabelSize"
            />
            <div class="uik-progress__progress">
                <div v-if="text1 || text2" class="uik-progress__line-text-wrapper">
                    <div
                        v-if="text1"
                        :title="text1"
                        class="uik-progress__line-text uik-progress__line-text--1"
                    >{{ text1 }}</div>
                    <div
                        v-if="text2"
                        :title="text2"
                        class="uik-progress__line-text uik-progress__line-text--2"
                    >{{ text2 }}</div>
                </div>

                <component
                    :is="tooltip ? 'UikTooltip' : 'div'"
                    class="uik-progress__line-wrapper"
                    :class="`uik-progress__line-wrapper--${getDirection}`"
                    :text="tooltip || null"
                >
                    <div
                        v-if="type === 'line'"
                        class="uik-progress__bar-wrapper"
                    >
                        <div class="uik-progress__bar"/>
                        <div
                            class="uik-progress__bar-progress"
                            :class="[
                                (`uik-progress__bar-progress--${color}`),
                                {'uik-progress__bar-progress--visible': !!getProgress}
                            ]"
                            :style="`width: ${getProgress}%`"
                        />
                    </div>
                    <div
                        v-if="type === 'steps'"
                        class="uik-progress__steps"
                    >
                        <div
                            v-for="step in getSteps"
                            :key="step"
                            class="uik-progress__step"
                        >
                            <div
                                class="uik-progress__step-progress"
                                :class="[
                                    (`uik-progress__step-progress--${color}`),
                                    {'uik-progress__step-progress--visible': step === 1 && getStepProgress(step)}
                                ]"
                                :style="`width: ${getStepProgress(step)}%`"
                            />
                        </div>
                    </div>
                </component>
            </div>
        </div>

        <div
            v-if="type === 'circle'"
            class="uik-progress__circle"
        >
            <div
                class="uik-progress__circle-progress"
                :class="`
                    uik-progress__circle-progress--${color}
                    uik-progress__circle-progress--${getDirection}
                `"
            >
                <svg>
                    <circle
                        :style="`--progress: ${getProgress}`"
                        :class="{'rounded': isRounded}"
                    />
                </svg>
                <div class="uik-progress__circle-text">
                    <span class="uik-progress__circle-percentage">{{ progress }}</span>
                    <span class="uik-progress__circle-percentage-symbol">%</span>
                </div>
            </div>
            <UikData
                v-if="label || text1 || text2"
                class="uik-progress__circle-label"
                :title="text1"
                :content="label"
                :subtitle="text2"
                :trend="labelTrend"
                :trend-type="labelTrendType"
                :trend-direction="labelTrendDirection"
                :layout="labelLayout"
                :size="getLabelSize"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: "UikProgress",
    props: {
        /**
         * Progress visualization
         */
        type: {
            type: String,
            default: "line",
            validator (value) {
                return [
                    "line",
                    "steps",
                    "circle"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Label next to progress
         * UikDataLabel for types 'line' and 'steps', UikData for type 'circle'
         */
        label: {
            type: String,
            default: ""
        },
        /**
         * Fill color indicator next to label in types 'line' and 'steps'
         */
        labelFill: {
            type: Boolean,
            default: false
        },
        /**
         * Size of label
         */
        labelSize: {
            type: String,
            default: "",
            validator (value) {
                return [
                    "",
                    "small",
                    "regular"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Trend content in label for type 'circle'
         */
        labelTrend: {
            type: String,
            default: ""
        },
        /**
         * Trend type in label for type 'circle'
         */
        labelTrendType: {
            type: String,
            default: "good",
            validator (value) {
                return [
                    "good",
                    "bad"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Trend direction in label for type 'circle'
         */
        labelTrendDirection: {
            type: String,
            default: "up",
            validator (value) {
                return [
                    "up",
                    "down"
                ].indexOf(value) !== -1
            }
        },
        labelLayout: {
            type: String,
            default: "vertical",
            validator (value) {
                return [
                    "vertical",
                    "horizontal"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Progress color, inherited by label in types 'line' and 'steps'
         */
        color: {
            type: String,
            default: "primary",
            validator (value) {
                return [
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "error",
                    "red",
                    "orange",
                    "yellow",
                    "lime",
                    "green",
                    "cyan",
                    "blue",
                    "purple",
                    "pink",
                    "gray"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Progress amount
         */
        progress: {
            type: [String, Number],
            default: 0,
            validator (value) {
                return !isNaN(parseFloat(value))
            }
        },
        /**
         * Direction of progress visualiztion
         * left/right for types 'line' and 'steps',
         * clockwise/counter-clockwise for type 'circle'
         */
        direction: {
            type: String,
            default: "",
            validator (value) {
                return [
                    "",
                    "left",
                    "right",
                    "clockwise",
                    "counter-clockwise"
                ].indexOf(value) !== -1
            }
        },
        /**
         * First additional text
         * Displayed on the left side, above the line in types 'line' and 'steps'
         * Displayed above label in type 'circle'
         */
        text1: {
            type: String,
            default: ""
        },
        /**
         * Second additional text
         * Displayed on the right side, above the line in types 'line' and 'steps'
         * Displayed below label in type 'circle'
         */
        text2: {
            type: String,
            default: ""
        },
        /**
         * Amount of steps in type 'steps'
         */
        steps: {
            type: [String, Number],
            default: 1,
            validator (value) {
                return !isNaN(parseFloat(value))
            }
        },
        /**
         * Tooltip that shows when hovering progress line
         * Only visible with types 'line' and 'steps'
         */
        tooltip: {
            type: String,
            default: ""
        }
    },
    computed: {
        /**
         * Get progress from 0 to 100
         *
         * @return {Number}
         */
        getProgress () {
            const progress = parseFloat(this.progress)
            if (progress < 0) return 0
            if (progress > 100) return 100

            return progress
        },
        /**
         * Get amount of steps to render
         *
         * @returns {Number}
         */
        getSteps () {
            let steps = parseFloat(this.steps)
            steps = Math.ceil(steps)

            return steps
        },
        /**
         * Get label size or fallback to type's default
         *
         * @returns {String}
         */
        getLabelSize () {
            const size = this.labelSize
            const type = this.type

            const defaultTypeSize = {
                line: "small",
                steps: "small",
                circle: "regular"
            }

            return size || defaultTypeSize[type]
        },
        /**
         * Get progress direction or fallback to type's default
         *
         * @returns {String}
         */
        getDirection () {
            const direction = this.direction
            const type = this.type

            const defaultTypeDirection = {
                line: "right",
                steps: "right",
                circle: "clockwise"
            }

            return direction || defaultTypeDirection[type]
        },
        /**
         * Does container expand to full width
         *
         * @returns {Boolean}
         */
        isFullWidth () {
            const type = this.type
            return type === "line" || type === "steps" || (
                type === "circle" &&
                this.labelLayout === "horizontal" &&
                (this.label || this.text1 || this.text2)
            )
        },
        /**
         * Is progress in circle rounded on the edges
         * Serves to disable rounded edges in high progress percetage since edges can overlap and create incorrect visualization
         *
         * @returns {Boolean}
         */
        isRounded () {
            return this.getProgress > 96
        }
    },
    methods: {
        /**
         * Gets progress for individual step
         *
         * @param {Number} step - index
         *
         * @returns {Number} width of step in percentage
         */
        getStepProgress (step) {
            const steps = this.getSteps
            const progress = this.getProgress

            if (!steps) return 0

            const max = 100 / steps * step
            const min = 100 / steps * (step - 1)

            let width = (progress - min) * 100 / (max - min)

            if (width > 100) width = 100
            if (width < 0) width = 0

            return width
        }
    }
}

</script>

<style lang="scss">
.uik-progress {
    &--full-width {
        width: 100%;
    }

    .uik-progress__line {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;
        width: 100%;

        .uik-progress__line-label {
            &.uik-data-label--small {
                transform: translateY(-1px);
            }
            & + .uik-progress__progress {
                margin-left: 25px;
            }
        }

        .uik-progress__progress {
            display: flex;
            flex-flow: column nowrap;
            justify-content: flex-start;
            align-items: flex-start;
            width: 100%;

            .uik-progress__line-text-wrapper {
                width: 100%;
                display: flex;
                flex-flow: row nowrap;
                justify-content: space-between;
                align-items: flex-end;
                padding: 0 1px;

                .uik-progress__line-text {
                    font-size: 13px;
                    line-height: 1.2;
                    font-weight: 500;
                    color: $textMain;
                    max-width: 50%;
                    word-break: break-word;
                    margin-bottom: 1px;

                    &--1 {
                        padding-right: 10px;
                        text-align: left;
                    }

                    &--2 {
                        padding-left: 10px;
                        text-align: right;
                        margin-left: auto;
                    }
                }
            }

            .uik-progress__line-wrapper {
                width: 100%;
                padding: 6px 0;

                .uik-progress__bar-wrapper {
                    $height: 6px;
                    height: $height;
                    display: flex;
                    align-items: center;
                    position: relative;
                    width: 100%;
                    min-width: 120px;

                    .uik-progress__bar {
                        width: 100%;
                        height: calc(#{$height} - 2px);
                        background: darken($borderColor, 4%);
                        border-radius: 99px;
                    }

                    .uik-progress__bar-progress {
                        position: absolute;
                        top: 0;
                        height: 100%;
                        border-radius: 99px;
                        transition: width 0.2s;

                        &--visible {
                            min-width: 6px;
                        }

                        @each $name, $color in $colors {
                            &--#{"" + $name} {
                                background: $color;
                            }
                        }

                        @each $name, $color in $projectColors {
                            &--#{"" + $name} {
                                background: $color;
                            }
                        }
                    }
                }

                .uik-progress__steps {
                    width: 100%;
                    display: flex;
                    flex-flow: row nowrap;
                    justify-content: flex-start;
                    align-items: center;
                    min-width: 120px;

                    .uik-progress__step {
                        flex-grow: 1;
                        flex-basis: 0;
                        display: flex;
                        flex-flow: row nowrap;
                        justify-content: flex-start;
                        height: 6px;
                        background: darken($borderColor, 6%);
                        overflow: hidden;

                        .uik-progress__step-progress {
                            width: 0;
                            height: 100%;

                            &--visible {
                                min-width: 6px;
                            }

                            @each $name, $color in $colors {
                                &--#{"" + $name} {
                                    background: $color;
                                }
                            }

                            @each $name, $color in $projectColors {
                                &--#{"" + $name} {
                                    background: $color;
                                }
                            }
                        }
                    }
                }

                &--right {
                    .uik-progress__bar-wrapper {
                        .uik-progress__bar-progress {
                            left: 0;
                        }
                    }

                    .uik-progress__steps {
                        .uik-progress__step {
                            justify-content: flex-start;

                            &:first-child {
                                border-top-left-radius: 99px;
                                border-bottom-left-radius: 99px;
                            }

                            &:last-child {
                                border-top-right-radius: 99px;
                                border-bottom-right-radius: 99px;
                            }

                            & + .uik-progress__step {
                                margin-left: 3px;
                            }
                        }
                    }
                }

                &--left {
                    .uik-progress__bar-wrapper {
                        .uik-progress__bar-progress {
                            right: 0;
                        }
                    }

                    .uik-progress__steps {
                        flex-direction: row-reverse;

                        .uik-progress__step {
                            justify-content: flex-end;

                            &:first-child {
                                border-radius: 0 99px 99px 0;
                            }

                            &:last-child {
                                border-radius: 99px 0 0 99px;
                            }

                            & + .uik-progress__step {
                                margin-right: 3px;
                            }
                        }
                    }
                }
            }
        }
    }

    .uik-progress__circle {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;

        .uik-progress__circle-progress {
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;
            position: relative;

            $size: 74px;
            $stroke: 6px;
            $pi: 3.15;
            $r: calc((#{$size} - #{$stroke}) / 2);
            $circ: calc(2 * #{$pi} * #{$r});

            width: $size;
            height: $size;
            min-width: $size;
            min-height: $size;

            &::before {
                content: "";
                position: absolute;
                top: 1px;
                left: 1px;
                width: calc(100% - 2px);
                height: calc(100% - 2px);
                border-radius: 50%;
                border: solid calc(#{$stroke} - 2px) darken($borderColor, 3.5%);
            }

            svg {
                position: absolute;
                width: 100%;
                height: 100%;
                min-width: 100%;
                min-height: 100%;

                circle {
                    left: 0;
                    top: 0;
                    width: 100%;
                    height: 100%;
                    fill: none;
                    stroke-width: $stroke;
                    transform: translate(calc(#{$stroke} / 2), calc(#{$stroke} / 2));
                    stroke-linecap: round;
                    cx: $r;
                    cy: $r;
                    r: $r;
                    stroke-dasharray: $circ;
                    stroke-dashoffset: calc(#{$circ} - (#{$circ} * var(--progress)) / 100);
                    transition: all 0.2s;

                    &.rounded {
                        stroke-linecap: initial;
                    }
                }
            }

            @each $name, $color in $colors {
                &--#{"" + $name} {
                    svg circle {
                        stroke: $color;
                    }
                }
            }

            @each $name, $color in $projectColors {
                &--#{"" + $name} {
                    svg circle {
                        stroke: $color;
                    }
                }
            }

            &--clockwise {
                svg {
                    transform: rotate(-90deg);
                }
            }

            &--counter-clockwise {
                svg {
                    transform: scaleX(-1) rotate(-90deg);
                }
            }

            .uik-progress__circle-text {
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: flex-start;
                position: relative;
                transform: translateX(-2px);

                .uik-progress__circle-percentage {
                    font-size: 21.5px;
                    font-weight: 600;
                    color: $textMain;
                    line-height: 24px;
                    text-align: center;
                }

                .uik-progress__circle-percentage-symbol {
                    position: absolute;
                    right: -11px;
                    top: 2px;
                    font-size: 12px;
                    line-height: 14px;
                    font-weight: 900;
                    color: $textMain;
                    text-align: left;
                }
            }
        }

        .uik-progress__circle-label {
            margin-left: 20px;
            transform: translateY(-1px);

            &.uik-data--regular {
                .uik-data__title {
                    & + .uik-data__content-wrapper {
                        margin-top: 5px;
                    }
                }

                .uik-data__content-wrapper {
                    & + .uik-data__subtitle {
                        margin-top: 4px;
                    }
                }
            }
        }
    }
}
</style>
